import './styles/Register.scss';

import { useApolloClient } from '@apollo/client';
import { Button, Divider, Grid } from '@mui/material';

import { Formik } from 'formik';
import { Gateway } from 'index';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import TEST_TAXID_QUERY from 'gql/queries/testTaxId.gql';

import { useCompaniesMutation } from 'api/hooks/jobmarket/companies';
import SubmittingLoader from 'app/components/Util/SubmittingLoader';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { actions as FormErrorActions } from 'app/containers/Dashboard/FormError/slice';
import { translations } from 'locales/i18n';
import { euCountries } from 'utils/Countries';
import { validateForm } from 'utils/FormErrorHelper';

import CompanyData from './register/CompanyData';
import CompanyDescription from './register/CompanyDescription';
import ContactData from './register/ContactData';
import FacultyData from './register/FacultyData';
import InvoiceData from './register/InvoiceData';
import RegisterStepper from './register/RegisterStepper';
import { registerValidationSchema } from './validation';

interface Props {
  onCancel: Function;
  setDisabledCancel: Function;
}

export default function Register(props: Props) {
  const [activeStep, setActiveStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const client = useApolloClient();

  const { create } = useCompaniesMutation();

  const isEmailTaken = async (value: string) => {
    const { data } = await Gateway.get(`/users/isEmailTaken?userEmail=${value}`);
    return data;
  };

  const handleStep = (direction: 'back' | 'next') => {
    switch (direction) {
      case 'back':
        if (activeStep <= 0) {
          setActiveStep(0);
        } else {
          setActiveStep(activeStep - 1);
        }
        break;
      case 'next':
        if (activeStep >= 4) {
          setActiveStep(4);
        } else {
          setActiveStep(activeStep + 1);
        }
        break;
      default:
        break;
    }
  };

  const renderStep = () => {
    switch (activeStep) {
      case 0:
        return <ContactData />;
      case 1:
        return <CompanyData />;
      case 2:
        return <InvoiceData />;
      case 3:
        return <CompanyDescription />;
      case 4:
        return <FacultyData />;
      default:
        return null;
    }
  };

  const stepValid = values => {
    const validationSchema = registerValidationSchema(t);
    switch (activeStep) {
      case 0:
        return validationSchema.contactData.isValidSync(values.contactData);
      case 1:
        return validationSchema.companyData.isValidSync(values.companyData);
      case 2:
        return validationSchema.invoiceData.isValidSync(values.invoiceData, {
          context: {
            country: values.companyData.country,
            useSameAddress: values.useSameAddress
          }
        });
      case 3:
        return validationSchema.description.isValidSync(values.description);
      case 4:
        return validationSchema.courses.isValidSync(values.courses);
      default:
        return false;
    }
  };

  const submitRegistration = async values => {
    if (submitting) {
      return;
    }
    if (props.setDisabledCancel) {
      props.setDisabledCancel(true);
    }
    setSubmitting(true);
    const emailTaken = await isEmailTaken(values.contactData.email);
    if (emailTaken) {
      setSubmitting(false);
      if (props.setDisabledCancel) {
        props.setDisabledCancel(false);
      }
      setActiveStep(0);
      return dispatch(AlertActions.error(t(translations.validation.emailTaken())));
    }

    if (
      values.companyData.country !== 'Germany' &&
      euCountries.includes(values.companyData.country)
    ) {
      dispatch(AlertActions.success(t(translations.common.validatingTaxId())));
      let dataTestTax: any = {};
      try {
        const response = await client.query({
          query: TEST_TAXID_QUERY,
          variables: { taxIdForeignCompany: values.invoiceData.taxId }
        });
        dataTestTax = response.data;
      } catch (err) {
        dispatch(
          AlertActions.error(
            t(translations.common.failedValidatingTaxId(), {
              cause: 'Failed fetching'
            })
          )
        );
        setSubmitting(false);
        return;
      }
      if (dataTestTax) {
        if (dataTestTax.testTaxId !== 200) {
          dispatch(
            AlertActions.error(
              t(translations.common.failedValidatingTaxId(), {
                cause: dataTestTax.testTaxId
              })
            )
          );
          setSubmitting(false);
          return;
        }
      }
    }

    const saveData = {
      ...values.companyData,
      description: values.description,
      mainContact: values.contactData,
      courses: values.courses.map(c => Number(c.id)),
      ...values.invoiceData,
      sameInvoiceaddress: values.useSameAddress,
      agreedToNewsletter: values.agreedToNewsletter
    };

    if (values.companyData.logo) {
      // Save logo first
      const formData = new FormData();
      formData.append('file', values.companyData.logo);
      const { data: fileURI } = await Gateway.post('/upload/logo', formData);
      saveData.logo = fileURI;
    }

    try {
      await create.mutateAsync(saveData, {
        onSuccess: () => {
          client.refetchQueries({ include: ['GetCompanies', 'GetJobOfferings'] });
        }
      });
      dispatch(AlertActions.success(t(translations.pages.login.alerts.registerSuccessful())));
      navigate('/login');
    } catch (err) {
      dispatch(AlertActions.error(t(translations.pages.login.alerts.registerFailed())));
    }
    setSubmitting(false);
    props.onCancel();
    if (props.setDisabledCancel) {
      props.setDisabledCancel(false);
    }
  };

  return (
    <Formik
      onSubmit={values => submitRegistration(values)}
      initialValues={{
        contactData: {},
        companyData: {
          country: ''
        },
        courses: [],
        description: null,
        invoiceData: {},
        useSameAddress: false
      }}
      validate={values => {
        const context = {
          useSameAddress: values.useSameAddress,
          country: values.companyData.country
        };
        return validateForm(
          values,
          yup.object().shape(registerValidationSchema(t)),
          dispatch,
          context
        );
      }}
    >
      {({ handleSubmit, values, errors, isValid }) => (
        <form onSubmit={handleSubmit}>
          <Grid container className="register-form">
            <Grid container className="register-form__steps">
              <RegisterStepper activeStep={activeStep} />
            </Grid>
            <Grid container>{renderStep()}</Grid>
            <Divider style={{ width: '100%', margin: '1em 0em' }} />
            <Grid container>
              <Grid container justifyContent="space-between" className="register-form__navigation">
                <Grid item xs={12} sm={6}>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      dispatch(FormErrorActions.close());
                      props.onCancel();
                    }}
                    disabled={submitting}
                  >
                    {t(translations.common.cancel())}
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Grid container justifyContent="flex-end">
                    {activeStep > 0 && (
                      <Button
                        variant="outlined"
                        color="primary"
                        disabled={submitting}
                        onClick={() => handleStep('back')}
                        style={{ marginRight: '1em' }}
                      >
                        {t(translations.common.back())}
                      </Button>
                    )}
                    {activeStep < 4 ? (
                      <>
                        <Button
                          key="next"
                          disabled={!stepValid(values) || submitting}
                          variant="contained"
                          color="primary"
                          onClick={() => {
                            handleStep('next');
                          }}
                          style={{ color: 'white' }}
                        >
                          {t(translations.common.next())}
                        </Button>
                      </>
                    ) : (
                      <span style={{ position: 'relative' }}>
                        <Button
                          key="submit"
                          disabled={!stepValid(values) || !isValid || submitting}
                          color="primary"
                          variant="contained"
                          type="submit"
                        >
                          {t(translations.pages.login.registerTitle())}
                        </Button>
                        {submitting && <SubmittingLoader />}
                      </span>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </form>
      )}
    </Formik>
  );
}
